import { predicateFor } from '@gonfalon/legacy-form-validation/validationUtils';
import { msg } from '@gonfalon/messages/msg';
import { getIsValidRedirectPath as getIsValidRedirectPathFromNavigator } from '@gonfalon/navigator/getIsValidRedirectPath';
import { isSafeInternalReturnPath } from '@gonfalon/navigator/isSafeInternalPath';
import { startsWith } from '@gonfalon/strings/startsWith';
import qs from 'qs';

export const DEFAULT_REDIRECT_PATH = '/';

export function isAbsolute(url: string) {
  // cannot use the parseURL above since the browser automatically prepends the current
  // page's protocol by default
  return /^(?:\w+:)?\/\//.test(url.toString());
}

export function isSubpathOrSelf(currentUrl: string, url: string) {
  return currentUrl === url || startsWith(currentUrl, url);
}

export function addQueryParams(url: string, additionalParams: object) {
  const [path, queryString] = url.split('?');
  const params = qs.parse(queryString);
  const withRelayState = qs.stringify({
    ...params,
    ...additionalParams,
  });
  return `${path}?${withRelayState}`;
}

export function removeQueryParam(queryString: string, clearedParam: string) {
  const parsedQueryString = qs.parse(queryString, { ignoreQueryPrefix: true });
  delete parsedQueryString[clearedParam];
  return qs.stringify(parsedQueryString, { indices: false });
}

export function appendPathSegments(path: string, ...segments: string[]) {
  const hashIndex = path.indexOf('#');
  const hash = hashIndex === -1 ? '' : path.slice(hashIndex);
  const pathAndSearch = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const searchIndex = pathAndSearch.indexOf('?');
  const search = searchIndex === -1 ? '' : pathAndSearch.slice(searchIndex);
  const pathname = searchIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, searchIndex);
  const suffix = segments
    .map((segment) => segment.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');

  return suffix ? `${pathname.replace(/\/+$/g, '')}/${suffix}${search}${hash}` : path;
}

export function isValidPort(url: string) {
  try {
    const port = new URL(url).port;
    return port === '' || port === '80' || port === '443';
  } catch {
    return false;
  }
}

export const urlWithRelayState = (url: string, relayUrl: string) => addQueryParams(url, { RelayState: relayUrl });

export const isAbsoluteURL = predicateFor(
  (v: string) => !isAbsolute(v.toLowerCase()),
  () => msg('ui.urlField.notAbsolute'),
);

export const isValidURLPort = predicateFor(
  (v: string) => !isValidPort(v.toLowerCase()),
  () => msg('ui.urlField.portNotAllowed'),
);

export const getIsValidRedirectPath = getIsValidRedirectPathFromNavigator;

export type ValidatedReturnUrlTarget = { kind: 'router'; path: string } | { kind: 'assign'; url: string };

function parseValidatedRedirectUrl(redirectPath: string): URL | undefined {
  if (!getIsValidRedirectPath(redirectPath)) {
    return undefined;
  }

  try {
    return new URL(redirectPath, window.location.origin);
  } catch {
    return undefined;
  }
}

/**
 * Resolves a validated return URL for client redirects. Same-origin targets are normalized to
 * in-app paths for React Router; cross-host acceptedRedirectHosts targets use full navigation.
 */
export function getValidatedReturnUrlTarget(raw: string | null | undefined): ValidatedReturnUrlTarget {
  if (!raw) {
    return { kind: 'router', path: DEFAULT_REDIRECT_PATH };
  }

  const url = parseValidatedRedirectUrl(raw);
  if (!url) {
    return { kind: 'router', path: DEFAULT_REDIRECT_PATH };
  }

  if (url.host === window.location.host) {
    if (!isSafeInternalReturnPath(raw)) {
      return { kind: 'router', path: DEFAULT_REDIRECT_PATH };
    }

    return { kind: 'router', path: url.pathname + url.search + url.hash };
  }

  return { kind: 'assign', url: url.href };
}

/**
 * Merges additional query parameters into a redirect path, handling both relative and absolute URLs.
 * Returns the original path unchanged if:
 * - The path is empty/undefined
 * - There are no params to merge
 * - The path cannot be parsed as a valid URL
 *
 * @param redirectPath - The base path (relative like "/dashboard?foo=bar" or absolute like "https://example.com")
 * @param paramsToMerge - URLSearchParams containing additional parameters to merge
 * @returns The path with merged query parameters, or the original path if merging isn't possible
 */
export function mergeQueryParamsIntoPath(redirectPath: string | undefined, paramsToMerge: URLSearchParams): string {
  if (!redirectPath) {
    return '';
  }

  // If no params to merge, return redirectPath as-is to avoid URL normalization (e.g., adding trailing slash)
  if (paramsToMerge.size === 0) {
    return redirectPath;
  }

  try {
    const url = new URL(redirectPath, window.location.origin);
    paramsToMerge.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    // For absolute URLs (external redirects), preserve the full href; for relative paths, use pathname + search + hash
    const lowerPath = redirectPath.toLowerCase();
    const isAbsoluteUrl = lowerPath.startsWith('http://') || lowerPath.startsWith('https://');
    return isAbsoluteUrl ? url.href : url.pathname + url.search + url.hash;
  } catch {
    // If URL parsing fails, return the original path unchanged
    return redirectPath;
  }
}
